import {Action, Module, Mutation} from 'vuex-module-decorators';
import {CategoryModel, ContestantsCountryCount} from '@/models/category.model';
import {CategoriesApi, HomeCategoriesResponse} from '@/api/categories.api';
import store from '@/store';
import {ChallengeModel} from '@/models/challenge.model';
import {take} from 'rxjs/operators';
import {EntityStateAdapter} from '@/store/base.store';

export const featureKey = 'categories';

// @ts-ignore
if (store.state.categories) {
  store.unregisterModule(featureKey)
}

interface UpsertCategoryChallenges {
  categoryId: number,
  challenges: Array<ChallengeModel>,
}

interface SetContestantsCountryCount {
  categoryId: number,
  contestantsCountryCount: Array<ContestantsCountryCount>,
}

@Module({
  store,
  dynamic: true,
  name: featureKey,
  namespaced: true,
})
export class CategoriesModule extends EntityStateAdapter<CategoryModel> {
  private _activeCategories: CategoryModel[] = [];

  @Mutation
  activeChallengesLoaded(categories: CategoryModel[]) {
    this._activeCategories = categories;
    this._loading = false;
  }

  @Mutation
  upsertCategoriesChallenges(data: UpsertCategoryChallenges) {
    if (this._entities) {
      const index = this._entities.findIndex(({id}) => id === data.categoryId);
      // tslint:disable-next-line: no-bitwise
      if (~index) {
        this._entities[index].challenges = data.challenges;
      }
    }
    this._loading = false;
  }

  @Mutation
  setContestantsCountryCount(data: SetContestantsCountryCount) {
    if (this._entities) {
      const index = this._entities.findIndex(({id}) => id === data.categoryId);
      // tslint:disable-next-line:no-bitwise
      if (~index) {
        this._entities[index].contestants_country_count = data.contestantsCountryCount;
      }
    }
    this._loading = false;
  }

  @Mutation
  setHomeCategories(categories: HomeCategoriesResponse[]) {
    const {_entities} = this;

    if (_entities) {
      categories.forEach((category) => {
        const index = _entities.findIndex(({id}) => id === category.id);
        if (index >= 0 && this._entities) {
          this._entities[index].contestants_country_count = (
            category.contestants_country_count
          );
        }
      })
    }
    this._loading = false;
  }

  @Action({commit: 'insertMany', rawError: true})
  async fetchCategories(): Promise<CategoryModel[]> {
    this.updateLoading(true);

    return await (new CategoriesApi())
      .all()
      .pipe(take(1))
      .toPromise();
  }

  @Action({commit: 'upsertCategoriesChallenges', rawError: true})
  async fetchCategoryChallenge(categoryId: number): Promise<{ categoryId: number, challenges: ChallengeModel[] }> {
    this.updateLoading(true);

    const challenges = await (new CategoriesApi())
      .getChallenges(categoryId)
      .pipe(take(1))
      .toPromise();
    return {categoryId, challenges};
  }

  @Action({commit: 'setHomeCategories', rawError: true})
  async fetchHomeCategories() {
    this.updateLoading(true);
    return (new CategoriesApi())
      .getHomeData()
      .pipe(take(1))
      .toPromise();
  }

  @Action
  getCategoryChallenges(id: number): ChallengeModel[] {
    const category = this.categories && this.categories.find((item) => item.id === id);
    return category && category.challenges ? category.challenges : [];
  }

  @Action({commit: 'activeChallengesLoaded', rawError: true})
  async fetchActiveCategories(): Promise<ChallengeModel[]> {
    this.updateLoading(true);
    return await (new CategoriesApi())
      .actives()
      .pipe(take(1))
      .toPromise()
  }

  get categories(): CategoryModel[] {
    return this._entities;
  }

  get activeCategories(): CategoryModel[] {
    return this._activeCategories;
  }


  get loading(): boolean {
    return super.loading;
  }

  get getCategoryBySlug(): (slug: string) => CategoryModel | null {
    return (slug: string) => this._activeCategories.find((category) => category.slug === slug) || null;
  }
}
